import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  MutateSetLapEncryptionCode,
  MutateSetLapEnergy,
  MutateSetLapTime,
  MutateSetLapEnergyEntry,
  MutateSetLapTimeEntry,
} from "../../../utils/graphql/hooks/raceDataHooks";
import { decode } from "../../../lib/encryption";
import {
  setLapEncryptionCodeLocal,
  setLapTimeEntryLocal,
  setLapEnergyEntryLocal,
} from "../../../state/raceDataReducer";
import { numericalString } from "../../../types";

interface RaceDataTableProps {
  carColour: string;
}

export default function RaceDataTable({ carColour }: RaceDataTableProps) {
  const dispatch = useDispatch();

  const encryptionConfig = useSelector(
    (state: RootState) => state.imports.derivedImportData.encryption?.config,
    isEqual,
  );

  const lapTable = useSelector(
    (state: RootState) => state.raceData[carColour]?.lapTable ?? {},
    isEqual,
  );

  const currentLap = useSelector(
    (state: RootState) =>
      state.raceData[carColour]?.raceConfig?.currentLap ?? null,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  // Get display name and colour for car from driver info
  const driverNumber = jagDrivers[carColour];
  const driver = driverNumber ? driverInfo[driverNumber] : null;
  const carConfig = {
    name: driver?.driverName || carColour,
    colour: driver?.driverHighlightColourAlt || "#888",
    fontColour: driver?.driverHighlightColourFontColourAlt || "white",
  };

  const driverDetails = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.participantDetails?.[
        driverNumber as numericalString
      ],
    isEqual,
  );

  const setLapEncryptionCode = MutateSetLapEncryptionCode();
  const setLapEnergy = MutateSetLapEnergy();
  const setLapTime = MutateSetLapTime();
  const setLapEnergyEntry = MutateSetLapEnergyEntry();
  const setLapTimeEntry = MutateSetLapTimeEntry();

  const handleEnergySubmit = (lapNumber: number, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      // Send null to clear energy
      setLapEnergy({
        variables: { carColour, lapNumber, energy: null },
      });
    } else {
      const energy = parseFloat(trimmedValue);
      if (!isNaN(energy)) {
        setLapEnergy({
          variables: {
            carColour,
            lapNumber,
            energy: parseFloat(energy.toFixed(2)),
          },
        });
      }
    }
  };

  const handleLapTimeSubmit = (lapNumber: number, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      setLapTime({
        variables: { carColour, lapNumber, lapTime: null },
      });
    } else {
      const lapTimeValue = parseFloat(trimmedValue);
      if (!isNaN(lapTimeValue)) {
        setLapTime({
          variables: {
            carColour,
            lapNumber,
            lapTime: parseFloat(lapTimeValue.toFixed(3)),
          },
        });
      }
    }
  };

  const handleCodeSubmit = (
    lapNumber: number,
    encryptionCode: string,
    encryptionColour: "Green" | "Orange",
  ) => {
    // Decode locally for instant UI update
    const decodedChannels = encryptionConfig
      ? decode(encryptionColour, encryptionCode, encryptionConfig)
      : null;

    dispatch(
      setLapEncryptionCodeLocal({
        carColour,
        lapNumber,
        encryptionCode,
        encryptionColour,
        decodedChannels,
      }),
    );

    // If decoded channels contain ERem and this lap doesn't have energy, set energy entry
    const lapHasEnergy = lapTable[lapNumber]?.energy != null;
    if (decodedChannels && "ERem" in decodedChannels) {
      const eRemValue = decodedChannels["ERem"];
      const predictedEnergy = lapTable[lapNumber]?.predictedEnergy;
      if (!lapHasEnergy && eRemValue != null && predictedEnergy) {
        // Check if eRem is sensible compared to the predicted energy
        let suggestedEnergy = null;
        const index =
          encryptionConfig?.[encryptionColour]?.channels.indexOf("ERem") ?? 0;
        const wrapping = encryptionConfig?.[encryptionColour]?.wrapping[index];

        const eRemNum =
          typeof eRemValue === "number" ? eRemValue : parseFloat(eRemValue);

        if (wrapping !== 10) {
          if (Math.abs(eRemNum - predictedEnergy) <= 0.5) {
            suggestedEnergy = eRemValue;
          }
        } else {
          // Only the units digit is known; search each possible tens digit for one that fits within range
          const maxEnergy =
            encryptionConfig?.[encryptionColour]?.max[index] ?? 0;
          const maxTens = Math.floor(maxEnergy / 10);
          for (let tens = 0; tens <= maxTens; tens++) {
            const candidate = tens * 10 + eRemNum;
            if (Math.abs(candidate - predictedEnergy) <= 0.5) {
              suggestedEnergy = candidate;
              break;
            }
          }
        }
        if (suggestedEnergy != null) {
          setLapEnergyEntry({
            variables: {
              carColour,
              lapNumber,
              energyEntry: String(suggestedEnergy),
            },
          });
        }
      }
    } else {
      if (!lapHasEnergy) {
        setLapEnergyEntry({
          variables: {
            carColour,
            lapNumber,
            energyEntry: null,
          },
        });
      }
    }

    // Convert decoded channels to array format for GraphQL
    const decodedChannelsArray = decodedChannels
      ? Object.entries(decodedChannels).map(([name, value]) => ({
          name,
          value: String(value),
        }))
      : null;

    // Persist to backend with decoded channels
    setLapEncryptionCode({
      variables: {
        carColour,
        lapNumber,
        encryptionCode,
        encryptionColour,
        decodedChannels: decodedChannelsArray,
      },
    });
  };

  // Only allow valid encryption code characters
  const VALID_CHARS = /^[0-9A-Za-z#%*=+]*$/;
  const filterCodeInput = (value: string) =>
    value
      .toUpperCase()
      .split("")
      .filter((c) => VALID_CHARS.test(c))
      .join("");

  // Only allow digits and up to dp decimal places
  const filterDecimalInput = (value: string, dp = 3) =>
    value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1")
      .replace(new RegExp(`(\\.\\d{${dp}}).*$`), "$1");

  // Check if code is invalid
  const isCodeInvalid = (code: string | undefined, expectedLength: number) => {
    if (!code || code.length === 0) return false;
    if (code.length !== expectedLength) return true;
    return !VALID_CHARS.test(code);
  };

  // Check if code has successfully decoded into channel values
  const isCodeDecoded = (
    decodedChannels: Record<string, string | number | null> | undefined,
  ) => decodedChannels != null && Object.keys(decodedChannels).length > 0;

  // Get channel IDs for short (green) and long (orange) codes from config
  const { greenChannelIds, orangeChannelIds } = useMemo(() => {
    if (!encryptionConfig) {
      return { greenChannelIds: [], orangeChannelIds: [] };
    }

    const greenChannelIds = encryptionConfig.Green.channels.filter(
      (id: string) => id && id.trim() !== "",
    );
    const orangeChannelIds = encryptionConfig.Orange.channels.filter(
      (id: string) => id && id.trim() !== "",
    );

    return { greenChannelIds, orangeChannelIds };
  }, [encryptionConfig]);

  // Get lap numbers directly from the lap table
  const lapNumbers = useMemo(() => {
    return Object.keys(lapTable)
      .map(Number)
      .sort((a, b) => a - b);
  }, [lapTable]);

  const totalColumns =
    3 +
    (greenChannelIds.length > 0 ? 1 + greenChannelIds.length : 0) +
    (orangeChannelIds.length > 0 ? 1 + orangeChannelIds.length : 0);

  const lastLap =
    currentLap !== null && lapTable[currentLap - 1] ? currentLap - 1 : null;
  const predictedCodeLap =
    driverDetails?.currentLoop !== undefined &&
    timingLoops &&
    driverDetails.currentLoop === Object.keys(timingLoops).length - 1 &&
    currentLap !== null
      ? currentLap
      : lastLap;

  // Calculate column widths - split remaining 90% evenly between green and orange (45% each)
  const greenColCount =
    greenChannelIds.length > 0 ? 2 + greenChannelIds.length : 1; // Energy always, Code + channels if present
  const orangeColCount =
    orangeChannelIds.length > 0 ? 1 + orangeChannelIds.length : 0;
  const greenColWidth = 45 / greenColCount;
  const orangeColWidth = orangeColCount > 0 ? 45 / orangeColCount : 0;

  return (
    <div className={styles.container}>
      <table>
        <colgroup>
          <col style={{ width: "3%", minWidth: "fit-content" }} />
          <col style={{ width: "7%", minWidth: "fit-content" }} />
          <col style={{ width: `${greenColWidth}%` }} />
          {greenChannelIds.length > 0 && (
            <>
              <col style={{ width: `${greenColWidth}%` }} />
              {greenChannelIds.map((id) => (
                <col
                  key={`col-green-${id}`}
                  style={{ width: `${greenColWidth}%` }}
                />
              ))}
            </>
          )}
          {orangeChannelIds.length > 0 && (
            <>
              <col style={{ width: `${orangeColWidth}%` }} />
              {orangeChannelIds.map((id) => (
                <col
                  key={`col-orange-${id}`}
                  style={{ width: `${orangeColWidth}%` }}
                />
              ))}
            </>
          )}
        </colgroup>
        <thead>
          <tr className={styles.titleRow}>
            <th
              colSpan={totalColumns}
              style={{
                backgroundColor: carConfig.colour,
                color: carConfig.fontColour,
              }}
            >
              {carConfig.name} - Race Data
            </th>
          </tr>
          <tr className={styles.titleRow}>
            <th colSpan={2}></th>
            {greenChannelIds.length > 0 && (
              <th
                colSpan={2 + greenChannelIds.length}
                className={styles.greenTitle}
              >
                Green Code
              </th>
            )}
            {orangeChannelIds.length > 0 && (
              <th
                colSpan={1 + orangeChannelIds.length}
                className={styles.orangeTitle}
              >
                Orange Code
              </th>
            )}
          </tr>
          <tr>
            <th>Lap</th>
            <th>Lap Time</th>
            <th className={styles.greenTitle}>Energy</th>
            {greenChannelIds.length > 0 && (
              <th className={styles.greenTitle}>Code</th>
            )}
            {greenChannelIds.map((id) => (
              <th key={`green-${id}`} className={styles.greenTitle}>
                {id}
              </th>
            ))}
            {orangeChannelIds.length > 0 && (
              <th className={styles.orangeTitle}>Code</th>
            )}
            {orangeChannelIds.map((id) => (
              <th key={`orange-${id}`} className={styles.orangeTitle}>
                {id}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lapNumbers.map((lapNum) => (
            <tr
              key={lapNum}
              className={
                lapNum === predictedCodeLap
                  ? styles.predictedCodeLap
                  : undefined
              }
              style={{
                ...(lapNum === currentLap
                  ? { backgroundColor: "#e5ccff", color: "black" }
                  : {}),
              }}
            >
              <td>{lapNum}</td>
              <td
                style={{
                  backgroundColor: (() => {
                    const confirmedLapTime = lapTable[lapNum]?.lapTime;
                    const lapTimeEntry = lapTable[lapNum]?.lapTimeEntry;
                    const hasConfirmedLapTime = confirmedLapTime != null;
                    const hasLapTimeEntry =
                      lapTimeEntry != null && lapTimeEntry !== "";

                    const lapTimeEntryNum = hasLapTimeEntry
                      ? parseFloat(lapTimeEntry)
                      : null;

                    let backgroundColor: string | undefined;
                    if (!hasLapTimeEntry && !hasConfirmedLapTime) {
                      backgroundColor = undefined;
                    } else if (lapTimeEntryNum === confirmedLapTime) {
                      backgroundColor = "#90EE90";
                    } else {
                      backgroundColor = "#FFFF99";
                    }

                    return backgroundColor;
                  })(),
                }}
              >
                <input
                  key={`laptime-input-${carColour}-${lapNum}`}
                  type="text"
                  inputMode="decimal"
                  className={styles.codeInput}
                  value={
                    lapTable[lapNum]?.lapTimeEntry != null
                      ? lapTable[lapNum].lapTimeEntry
                      : lapTable[lapNum]?.lapTime != null
                      ? String(lapTable[lapNum].lapTime)
                      : ""
                  }
                  placeholder={
                    lapTable[lapNum]?.predictedLapTime?.toFixed(3) ?? ""
                  }
                  onChange={(e) => {
                    const filtered = filterDecimalInput(e.target.value);
                    // Update locally for instant feedback
                    dispatch(
                      setLapTimeEntryLocal({
                        carColour,
                        lapNumber: lapNum,
                        lapTimeEntry: filtered,
                      }),
                    );
                    setLapTimeEntry({
                      variables: {
                        carColour,
                        lapNumber: lapNum,
                        lapTimeEntry: filtered,
                      },
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleLapTimeSubmit(lapNum, e.currentTarget.value);
                      e.currentTarget.blur();
                    }
                  }}
                />
              </td>
              <td
                style={{
                  backgroundColor: (() => {
                    const confirmedEnergy = lapTable[lapNum]?.energy;
                    const energyEntry = lapTable[lapNum]?.energyEntry;
                    const hasConfirmedEnergy = confirmedEnergy != null;
                    const hasEnergyEntry =
                      energyEntry != null && energyEntry !== "";

                    const energyEntryNum = hasEnergyEntry
                      ? parseFloat(energyEntry ?? "")
                      : null;

                    let backgroundColor: string | undefined;
                    if (!hasEnergyEntry && !hasConfirmedEnergy) {
                      backgroundColor = undefined;
                    } else if (energyEntryNum === confirmedEnergy) {
                      backgroundColor = "#90EE90";
                    } else {
                      backgroundColor = "#FFFF99";
                    }

                    return backgroundColor;
                  })(),
                }}
              >
                <input
                  key={`energy-input-${carColour}-${lapNum}`}
                  type="text"
                  inputMode="decimal"
                  className={styles.codeInput}
                  value={
                    lapTable[lapNum]?.energyEntry != null
                      ? lapTable[lapNum].energyEntry
                      : lapTable[lapNum]?.energy != null
                      ? String(lapTable[lapNum].energy)
                      : ""
                  }
                  placeholder={
                    lapTable[lapNum]?.predictedEnergy?.toFixed(2) ?? ""
                  }
                  onChange={(e) => {
                    const filtered = filterDecimalInput(e.target.value, 2);
                    // Update locally for instant feedback
                    dispatch(
                      setLapEnergyEntryLocal({
                        carColour,
                        lapNumber: lapNum,
                        energyEntry: filtered,
                      }),
                    );
                    setLapEnergyEntry({
                      variables: {
                        carColour,
                        lapNumber: lapNum,
                        energyEntry: filtered,
                      },
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEnergySubmit(lapNum, e.currentTarget.value);
                      e.currentTarget.blur();
                    }
                  }}
                />
              </td>
              {greenChannelIds.length > 0 && (
                <td
                  style={
                    isCodeInvalid(
                      lapTable[lapNum]?.greenCode?.encryptionCode,
                      4,
                    )
                      ? { backgroundColor: "#ff6b6b" }
                      : isCodeDecoded(
                          lapTable[lapNum]?.greenCode?.decodedChannels,
                        )
                      ? { backgroundColor: "#90EE90" }
                      : undefined
                  }
                >
                  <input
                    key={`green-input-${carColour}-${lapNum}`}
                    type="text"
                    className={styles.codeInput}
                    maxLength={4}
                    value={lapTable[lapNum]?.greenCode?.encryptionCode || ""}
                    onChange={(e) =>
                      handleCodeSubmit(
                        lapNum,
                        filterCodeInput(e.target.value),
                        "Green",
                      )
                    }
                  />
                </td>
              )}
              {greenChannelIds.map((id) => (
                <td key={`green-${lapNum}-${id}`}>
                  {lapTable[lapNum]?.greenCode?.decodedChannels?.[id] ?? "-"}
                </td>
              ))}
              {orangeChannelIds.length > 0 && (
                <td
                  style={
                    isCodeInvalid(
                      lapTable[lapNum]?.orangeCode?.encryptionCode,
                      5,
                    )
                      ? { backgroundColor: "#ff6b6b" }
                      : isCodeDecoded(
                          lapTable[lapNum]?.orangeCode?.decodedChannels,
                        )
                      ? { backgroundColor: "#90EE90" }
                      : undefined
                  }
                >
                  <input
                    key={`orange-input-${carColour}-${lapNum}`}
                    type="text"
                    className={styles.codeInput}
                    maxLength={5}
                    value={lapTable[lapNum]?.orangeCode?.encryptionCode || ""}
                    onChange={(e) =>
                      handleCodeSubmit(
                        lapNum,
                        filterCodeInput(e.target.value),
                        "Orange",
                      )
                    }
                  />
                </td>
              )}
              {orangeChannelIds.map((id) => (
                <td key={`orange-${lapNum}-${id}`}>
                  {lapTable[lapNum]?.orangeCode?.decodedChannels?.[id] ?? "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
